import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import get from 'lodash.get';
// Components
import {
    Button,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    Grid,
    Typography,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import FormInput from 'Console/components/common/form/FormInput';
import FormItemSelect from 'Console/components/common/form/FormItemSelect';

import { pushNotificationToStack } from 'Console/data/notification/actions';
// Constants
import { ADD_ON_CONFIGURATION, ADD_ON_AUTHENTICATION_TYPE } from 'Console/constants/appConstants';

const styles = (theme) => ({
    items: {
        paddingBottom: theme.typography.pxToRem(20),
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            marginRight: 0,
            paddingBottom: theme.typography.pxToRem(20),
        },
    },
    formInput: {
        flexBasis: '47%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: '5%',
        },
    },
    hiddenInput: {
        position: 'absolute',
        left: 10000,
        top: 10000,
    },
    positionInput: {
        overflow: 'hidden',
        position: 'relative',
    },
    formName: {
        width: '15%',
        marginRight: '3%',
        fontSize: theme.typography.pxToRem(14),
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginRight: 0,
        },
    },
});

const AddOnConfiguration = ({ classes, form, addon, intl }) => {
    const { getFieldValue } = form;
    const authType = get(addon, 'configuration.authentication.type') || ADD_ON_CONFIGURATION[0].value;
    const dispatch = useDispatch();

    const authorizationCodeUrl = get(addon, 'configuration.authentication.payload.authorizationCodeUrl');
    const redirectUri = get(addon, 'configuration.authentication.payload.redirectUri');

    const copyToClipboard = useCallback(
        (id) => () => {
            const copyText = document.getElementById(id);
            copyText.focus();
            copyText.select();
            document.execCommand('copy');

            if (id === 'clipboardInput') {
                dispatch(
                    pushNotificationToStack(
                        `${intl.formatMessage({ id: 'Notification.script.copied' })}`
                    )
                );
            } else {
                dispatch(
                    pushNotificationToStack(
                        `${intl.formatMessage({ id: 'Notification.configuration.copied' })}`
                    )
                );
            }
        },
        [dispatch, intl]
    );

    return (
        <Grid container>
            <Grid container direction="row" alignItems="center" className={classes.items}>
                <Typography className={classes.formName} variant="body1">
                    <FormattedMessage
                        id="Settings.addOn.detail.authentication"
                        defaultMessage="Authentication"
                    />
                </Typography>

                <Grid className={classes.formInput}>
                    <FormItemSelect
                        initialValue={authType}
                        form={form}
                        fullWidth={false}
                        formItemKey="addon.authType"
                        optionList={ADD_ON_CONFIGURATION}
                    />
                </Grid>
            </Grid>

            {/* ================= API KEY =============== */}
            {getFieldValue('addon.authType') === ADD_ON_AUTHENTICATION_TYPE.API_KEY && (
                <Grid container direction="row" alignItems="center" className={classes.items}>
                    <Typography className={classes.formName} variant="body1">
                        <FormattedMessage id="Settings.addOn.detail.apiKey" defaultMessage="Api Key" />
                    </Typography>

                    <Grid className={classes.formInput}>
                        <FormInput
                            isRequired
                            fullWidth={false}
                            form={form}
                            type="password"
                            formItemKey="addon.auth.apiKey"
                            placeholder="********"
                        />
                    </Grid>
                </Grid>
            )}

            {/* ================= USERNAME AND PASSWORD =============== */}
            {getFieldValue('addon.authType') === ADD_ON_AUTHENTICATION_TYPE.USERNAME_PASSWORD && (
                <>
                    <Grid container direction="row" alignItems="center" className={classes.items}>
                        <Typography className={classes.formName} variant="body1">
                            <FormattedMessage
                                id="Settings.addOn.detail.username"
                                defaultMessage="Username"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            <FormInput
                                isRequired
                                fullWidth={false}
                                form={form}
                                type="text"
                                formItemKey="addon.auth.username"
                                placeholder="Adopt"
                            />
                        </Grid>
                    </Grid>

                    <Grid container direction="row" alignItems="center" className={classes.items}>
                        <Typography className={classes.formName} variant="body1">
                            <FormattedMessage
                                id="Settings.addOn.detail.password"
                                defaultMessage="Password"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            <FormInput
                                isRequired
                                fullWidth={false}
                                form={form}
                                type="password"
                                formItemKey="addon.auth.password"
                                placeholder="********"
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            {/* ================= OAUTH2 CODE =============== */}
            {getFieldValue('addon.authType') ===
                ADD_ON_AUTHENTICATION_TYPE.OAUTH2_AUTHORIZATION_CODE && (
                <>
                    <Grid container direction="row" alignItems="center" className={classes.items}>
                        <Typography className={classes.formName} variant="body1">
                            <FormattedMessage
                                id="Settings.addOn.detail.clientID"
                                defaultMessage="Client ID"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            <FormInput
                                isRequired
                                fullWidth={false}
                                form={form}
                                type="text"
                                formItemKey="addon.auth.clientId"
                                placeholder="Client ID"
                            />
                        </Grid>
                    </Grid>

                    <Grid container direction="row" alignItems="center" className={classes.items}>
                        <Typography className={classes.formName} variant="body1">
                            <FormattedMessage
                                id="Settings.addOn.detail.clientSecret"
                                defaultMessage="Client secret"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            <FormInput
                                isRequired
                                fullWidth={false}
                                form={form}
                                type="password"
                                formItemKey="addon.auth.clientSecret"
                                placeholder="********"
                            />
                        </Grid>
                    </Grid>

                    {redirectUri ? (
                        <Grid container direction="row" alignItems="center" className={classes.items}>
                            <Typography className={classes.formName} variant="body1">
                                <FormattedMessage
                                    id="Settings.addOn.detail.redirectUri"
                                    defaultMessage="Redirect URI"
                                />
                            </Typography>

                            <Grid item className={classes.formInput}>
                                <div className={classes.positionInput}>
                                    <input
                                        className={classes.hiddenInput}
                                        type="text"
                                        value={redirectUri}
                                        id="clipboardInputRedirectUri"
                                    />
                                </div>
                                <FormControl fullWidth>
                                    <Input
                                        type="text"
                                        value={redirectUri}
                                        endAdornment={
                                            <InputAdornment>
                                                <IconButton
                                                    color="primary"
                                                    onClick={copyToClipboard(
                                                        'clipboardInputRedirectUri'
                                                    )}
                                                >
                                                    <FileCopyOutlinedIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    ) : null}

                    {authorizationCodeUrl ? (
                        <Grid container direction="row" alignItems="center" className={classes.items}>
                            <Typography className={classes.formName} variant="body1">
                                <FormattedMessage
                                    id="Settings.addOn.detail.activate"
                                    defaultMessage="Activate"
                                />
                            </Typography>

                            <Grid className={classes.formInput}>
                                <Button
                                    id="yeself-button"
                                    variant="contained"
                                    color="primary"
                                    href={authorizationCodeUrl}
                                    target="_blank"
                                    rel="noopener"
                                >
                                    Activate
                                </Button>
                            </Grid>
                        </Grid>
                    ) : null}
                </>
            )}
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(AddOnConfiguration));
